import { useCallback, useMemo } from 'react'
import { withQuery } from 'ufo'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { openPaymentDialog } from 'src/components/Payment/PaymentDialog'
import {
  Currency,
  type GetAllPaymentItemsQuery,
  Interval,
} from 'src/services/apollo/generated/graphql'
import {
  type MembershipPlan,
  useMembershipPlans,
} from 'src/services/helpers/membership'
import { handlePaymentError } from 'src/services/helpers/payment'
import { formatPrice, useCurrency } from 'src/utils/currency'
import { useMatches } from 'src/utils/router/route'
import { ensureLogIn } from 'src/utils/user'
import { PLAN_DISPLAY } from '../constants'

export interface LandingPlanDisplay {
  plan: MembershipPlan
  name: string
  featured: boolean
  perMonth: string
  perMonthOriginal: string | null
  perYear: string
  tier: number
  /** Whether this plan has pricing data and can be purchased */
  available: boolean
}

export function useLandingPricing(
  interval: Interval,
  opts?: {
    /**
     * Payment items pre-fetched on the server by `fetchPaymentItemsForSSR`.
     * Used as a fallback so the Landing SSR HTML ships real prices before
     * the client-side `useGetAllPaymentItems` (fetchPolicy: 'no-cache')
     * query resolves.
     */
    initialPaymentItems?: GetAllPaymentItemsQuery['paymentItems']
  },
) {
  const plans = useMembershipPlans({
    initialPaymentItems: opts?.initialPaymentItems,
  })
  const [currency] = useCurrency()
  const matches = useMatches()

  const displayPlans = useMemo((): LandingPlanDisplay[] => {
    return plans
      .filter(p => PLAN_DISPLAY[p.tier])
      .sort((a, b) => a.tier - b.tier)
      .map(plan => {
        const display = PLAN_DISPLAY[plan.tier]
        const prices = plan.prices?.[interval]?.[currency]
        return {
          plan,
          name: display.name,
          featured: display.featured ?? false,
          perMonth: prices ? formatPrice(currency, prices.perMonth) : '--',
          perMonthOriginal:
            prices && prices.perMonthOriginal !== prices.perMonth
              ? formatPrice(currency, prices.perMonthOriginal)
              : null,
          perYear: prices ? formatPrice(currency, prices.perYear) : '--',
          tier: plan.tier,
          available: !!prices,
        }
      })
  }, [plans, interval, currency])

  const subscribe = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await ensureLogIn()
      } catch {
        return // user dismissed login
      }

      const prices = plan.prices?.[interval]
      if (!prices) return

      const priceRecord = Object.fromEntries(
        Object.entries(prices).map(([cur, p]) => [
          cur,
          interval === Interval.Year ? p.perYear : p.perMonth,
        ]),
      ) as Record<Currency, number>

      try {
        await openPaymentDialog({
          itemId: plan.id,
          oneTimeMembership: plan.oneTimeMembership,
          interval,
          currency,
          prices: priceRecord,
          paymentProvider: { allowed: 'any' },
          redirect: new URL(
            withQuery('/payment/subscription/landing', {
              itemId: plan.id,
              interval,
              currency,
              price: priceRecord[currency],
              usdPrice: priceRecord[Currency.Usd],
            }),
            window.location.origin,
          ).toString(),
          dialogTrackingOpts: {
            scene: (matches.at(-1)?.handle as { name?: string })?.name,
          },
        })
      } catch (err) {
        if (err === UserCancelled) return
        handlePaymentError(err)
      }
    },
    [interval, currency, matches],
  )

  return { plans: displayPlans, subscribe, currency }
}
